import { useState, useEffect } from 'react';
import { Search, X, ChevronDown, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import type { ProductFilters as Filters, Category, Store, ProductType } from '../types';

interface ProductFiltersProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  stores: Store[];
  categories: Category[];
  productCounts?: {
    total: number;
    active: number;
    inactive: number;
    lowStock: number;
  };
}

const PRODUCT_TYPES: { value: ProductType; label: string }[] = [
  { value: 'PHYSICAL', label: 'Physical' },
  { value: 'DIGITAL', label: 'Digital' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'EXTERNAL', label: 'External' },
];

export function ProductFilters({
  filters,
  onChange,
  stores,
  categories,
  productCounts,
}: ProductFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const queryClient = useQueryClient();
  
  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string; storeId?: string }) => 
      api.post<{ category: Category }>('/products/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewCategoryName('');
      setShowAddCategory(false);
      toast.success('Category created');
    },
    onError: (error: any) => {
      toast.error(error.data?.error || 'Failed to create category');
    },
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onChange({ ...filters, search: searchValue || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setSearchValue('');
    onChange({});
  };

  const hasFilters =
    filters.search ||
    filters.storeId ||
    filters.categoryId ||
    filters.status ||
    filters.type ||
    filters.featured !== undefined ||
    filters.lowStock !== undefined ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined;

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Build category tree
  const rootCategories = categories.filter((c) => !c.parentId);
  const getChildren = (parentId: string) =>
    categories.filter((c) => c.parentId === parentId);

  const renderCategory = (category: Category, depth = 0) => {
    const children = getChildren(category.id);
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = filters.categoryId === category.id;

    return (
      <div key={category.id}>
        <button
          onClick={() => {
            if (children.length > 0) {
              toggleCategory(category.id);
            }
            updateFilter('categoryId', isSelected ? undefined : category.id);
          }}
          className={clsx(
            'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors',
            isSelected
              ? 'bg-orange-100 text-orange-700 border border-orange-300'
              : 'text-slate-700 hover:text-slate-900 hover:bg-gray-100'
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          {children.length > 0 && (
            <span className="text-slate-500">
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
          <span className="truncate flex-1">{category.name}</span>
          {category._count?.products !== undefined && (
            <span className="text-xs text-slate-500">{category._count.products}</span>
          )}
        </button>
        {children.length > 0 && isExpanded && (
          <div className="ml-2">
            {children.map((child) => renderCategory(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search products..."
          className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-10 py-2.5 text-slate-900 placeholder:text-slate-400"
        />
        {searchValue && (
          <button
            onClick={() => setSearchValue('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Clear Filters */}
      {hasFilters && (
        <button
          onClick={clearFilters}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-slate-700 hover:text-slate-900 hover:border-gray-400 transition-colors"
        >
          <X size={16} />
          Clear All Filters
        </button>
      )}

      {/* Store Filter */}
      {stores.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">
            Store
          </label>
          <select
            value={filters.storeId || ''}
            onChange={(e) => updateFilter('storeId', e.target.value || undefined)}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm"
          >
            <option value="">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} {store._count ? `(${store._count.products})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Status Filter */}
      <div>
        <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">
          Status
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All', count: productCounts?.total },
            { value: 'active', label: 'Active', count: productCounts?.active },
            { value: 'inactive', label: 'Inactive', count: productCounts?.inactive },
          ].map((status) => (
            <button
              key={status.value}
              onClick={() =>
                updateFilter(
                  'status',
                  filters.status === status.value ? undefined : (status.value as any)
                )
              }
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                filters.status === status.value || (!filters.status && status.value === 'all')
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-slate-700 hover:text-slate-900 hover:bg-gray-200 border border-gray-300'
              )}
            >
              {status.label}
              {status.count !== undefined && (
                <span className="ml-1 opacity-70">({status.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Low Stock Toggle */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.lowStock || false}
            onChange={(e) => updateFilter('lowStock', e.target.checked || undefined)}
            className="rounded border-slate-600"
          />
          <span className="text-sm text-slate-700">
            Low Stock Only
            {productCounts?.lowStock !== undefined && (
              <span className="ml-1 text-amber-400">({productCounts.lowStock})</span>
            )}
          </span>
        </label>
      </div>

      {/* Featured Toggle */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.featured || false}
            onChange={(e) => updateFilter('featured', e.target.checked || undefined)}
            className="rounded border-slate-600"
          />
          <span className="text-sm text-slate-700">Featured Only</span>
        </label>
      </div>

      {/* Product Type */}
      <div>
        <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">
          Product Type
        </label>
        <div className="flex flex-wrap gap-2">
          {PRODUCT_TYPES.map((type) => (
            <button
              key={type.value}
              onClick={() =>
                updateFilter('type', filters.type === type.value ? undefined : type.value)
              }
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                filters.type === type.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-slate-700 hover:text-slate-900 hover:bg-gray-200 border border-gray-300'
              )}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">
          Price Range
        </label>
        <div className="space-y-2">
          <input
            type="number"
            value={filters.minPrice ?? ''}
            onChange={(e) =>
              updateFilter('minPrice', e.target.value ? parseFloat(e.target.value) : undefined)
            }
            placeholder="Min"
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm"
            min="0"
          />
          <input
            type="number"
            value={filters.maxPrice ?? ''}
            onChange={(e) =>
              updateFilter('maxPrice', e.target.value ? parseFloat(e.target.value) : undefined)
            }
            placeholder="Max"
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm"
            min="0"
          />
        </div>
      </div>

      {/* Categories */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider">
            Categories
          </label>
          <button
            onClick={() => setShowAddCategory(!showAddCategory)}
            className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
        
        {/* Add Category Form */}
        {showAddCategory && (
          <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-slate-900 mb-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newCategoryName.trim()) {
                  createCategoryMutation.mutate({ 
                    name: newCategoryName.trim(),
                    storeId: filters.storeId 
                  });
                }
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (newCategoryName.trim()) {
                    createCategoryMutation.mutate({ 
                      name: newCategoryName.trim(),
                      storeId: filters.storeId 
                    });
                  }
                }}
                disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs py-1.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {createCategoryMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <Plus size={14} />
                    Create
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddCategory(false);
                  setNewCategoryName('');
                }}
                className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {categories.length > 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 max-h-64 overflow-y-auto">
            <button
              onClick={() => updateFilter('categoryId', undefined)}
              className={clsx(
                'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors',
                !filters.categoryId
                  ? 'bg-orange-100 text-orange-700 border border-orange-300'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-gray-100'
              )}
            >
              All Categories
            </button>
            {rootCategories.map((category) => renderCategory(category))}
          </div>
        ) : (
          <p className="text-xs text-slate-500 italic">
            No categories yet. Click "Add" to create one.
          </p>
        )}
      </div>
    </div>
  );
}
